import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { FEATURES } from 'shared/constants/features.constants';
import { CLASSIFICATION } from 'shared/constants/organization.constants';
import { currentEnvironment } from 'shared/services/Environment';
import organizationApiPrivate from '@/api/organization.api.private';
import AuthenticationService from '@/lib/auth';
import { FEATURE_LIST, FEATURE_SECTIONS, JIRA_BROWSE_URL } from '@/constants/feature-map.constants';
import { useOrganizationContext } from '../organization.context';

// Hub features are managed in hub-details.jsx
const hubFeatures = [FEATURES.KNOWLEDGE_HUB_ACCESS, FEATURES.KNOWLEDGE_HUB_SHARED];
const features = FEATURE_LIST.filter(feature => !hubFeatures.includes(feature.key));
const knownFeatureKeys = FEATURE_LIST.map(feature => feature.key);

const isDevFeature = feature => feature.kind === 'rollout' && feature.stage === 'dev';

function getLockReason(feature, { isOn, isInternal, isProduction, region }) {
  if (feature.kind === 'unlisted') {
    return "Unknown to back-office, can't be changed here";
  }

  // Turning a flag off is always allowed, so a misconfigured org can be cleaned up
  if (isOn) return null;

  if (isDevFeature(feature) && isProduction) {
    return "In development: can't be turned on in production. Move it to beta first";
  }

  if (isDevFeature(feature) && !isInternal) {
    return 'In development: internal organizations only';
  }

  if (feature.regions && !feature.regions.includes(region)) {
    return `Not available in the ${region} region`;
  }

  return null;
}

function FeatureBadges({ feature }) {
  return (
    <>
      {feature.regions?.map(region => (
        <Badge key={region} variant="outline" className="font-normal">
          {region} only
        </Badge>
      ))}
      {feature.kind === 'rollout' && feature.stage === 'ga' && (
        <Badge variant="outline" className="font-normal">
          <a href={`${JIRA_BROWSE_URL}/${feature.removalTicket}`} target="_blank" rel="noreferrer">
            Removal: {feature.removalTicket}
          </a>
        </Badge>
      )}
    </>
  );
}

function FeatureItem({ feature, isOn, onSaved, lockContext }) {
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  const labelId = useId();
  const descriptionId = useId();

  const lockReason = getLockReason(feature, { ...lockContext, isOn });

  const toggleFeature = async () => {
    const newState = !isOn;

    onSaved(feature.key, newState);
    setIsSaving(true);

    const res = newState
      ? await organizationApiPrivate.activateFeature({ feature: feature.key })
      : await organizationApiPrivate.deactivateFeature({ feature: feature.key });

    setIsSaving(false);

    if (res.error) {
      onSaved(feature.key, !newState);
      toast({ status: 'error', title: res.data.message, variant: 'destructive' });
      return;
    }

    toast({
      status: 'success',
      title: `Feature ${feature.label} ${newState ? 'activated' : 'deactivated'} successfully`,
    });
  };

  return (
    <div className="flex justify-between gap-4 border-t pt-4 first:border-t-0 first:pt-0 items-center">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h4 id={labelId} className="font-bold text-xs">
            {feature.label}
          </h4>
          <FeatureBadges feature={feature} />
        </div>
        <p id={descriptionId} className="font-normal leading-snug text-muted-foreground text-xs">
          {feature.description}
          {lockReason && <span className="block italic">{lockReason}</span>}
        </p>
      </div>
      <Switch
        checked={isOn}
        // Blocks a second toggle until the first is saved, so a failed request
        // can't roll the switch back over a newer click
        disabled={isSaving || Boolean(lockReason)}
        onCheckedChange={toggleFeature}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
      />
    </div>
  );
}

function FeatureSection({ section, sectionFeatures, enabledKeys, onSaved, lockContext }) {
  const [isOpen, setIsOpen] = useState(Boolean(section.defaultOpen));
  const onCount = sectionFeatures.filter(feature => enabledKeys.includes(feature.key)).length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <section className="overflow-hidden rounded-md border">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 bg-muted/50 px-4 py-3 text-left">
          <div>
            <h3 className="font-semibold text-base">
              {section.title}{' '}
              <span className="text-muted-foreground font-normal">
                ({onCount} of {sectionFeatures.length} on)
              </span>
            </h3>
            <p className="text-muted-foreground text-xs">{section.description}</p>
          </div>
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="flex flex-col gap-4 border-t p-4">
          {sectionFeatures.map(feature => (
            <FeatureItem
              key={feature.key}
              feature={feature}
              isOn={enabledKeys.includes(feature.key)}
              onSaved={onSaved}
              lockContext={lockContext}
            />
          ))}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

// Flags stored on the org that back-office doesn't know, e.g. ones only the backend
// defines. Listed so they're visible until they are removed.
const UNLISTED_SECTION = {
  id: 'unlisted',
  title: 'Unlisted',
  description: "On for this organization, but unknown to back-office. Can't be changed here.",
};

function FeaturesList({ organization }) {
  const [enabledKeys, setEnabledKeys] = useState(organization.features || []);

  const lockContext = {
    isInternal: organization.classification === CLASSIFICATION.INTERNAL,
    isProduction: currentEnvironment() === 'production',
    region: AuthenticationService.region,
  };

  const onSaved = (key, isOn) =>
    setEnabledKeys(keys => (isOn ? [...keys, key] : keys.filter(k => k !== key)));

  // Hidden: dev flags in production (a flag moves to beta before it's used there) and
  // flags outside their region. One that is already on stays visible so it can be
  // turned off, and stays until reload rather than vanishing when switched off.
  const isHidden = feature => {
    if (organization.features?.includes(feature.key)) return false;

    const isDevInProduction = isDevFeature(feature) && lockContext.isProduction;
    const isOutsideRegion = feature.regions && !feature.regions.includes(lockContext.region);

    return isDevInProduction || isOutsideRegion;
  };

  const sections = FEATURE_SECTIONS.map(section => ({
    section,
    sectionFeatures: features.filter(feature => section.includes(feature) && !isHidden(feature)),
  }));

  const unlistedFeatures = enabledKeys
    .filter(key => !knownFeatureKeys.includes(key))
    .map(key => ({ key, kind: 'unlisted', label: key, description: '' }));

  return [...sections, { section: UNLISTED_SECTION, sectionFeatures: unlistedFeatures }]
    .filter(({ sectionFeatures }) => sectionFeatures.length)
    .map(({ section, sectionFeatures }) => (
      <FeatureSection
        key={section.id}
        section={section}
        sectionFeatures={sectionFeatures}
        enabledKeys={enabledKeys}
        onSaved={onSaved}
        lockContext={lockContext}
      />
    ));
}

export default function FeaturesBox() {
  const { organization } = useOrganizationContext();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Features</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Keyed by org so switching organizations starts from the new org's flags */}
        <FeaturesList key={organization.id} organization={organization} />
      </CardContent>
    </Card>
  );
}
